'use client';
import { useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PLP_DEFAULT_SORT, PLP_SORT_OPTIONS, PlpSortOption } from './plp-data';
import { usePlpFilters } from './plp-use-filters';

interface PlpSortProps {
  /** Current sort value (already validated by resolveSort). */
  value: string;
  /** Label for the default option — "Featured" on a category, "Relevance" on search. */
  defaultLabel?: string;
}

const optionsWithDefault = (defaultLabel?: string): PlpSortOption[] =>
  PLP_SORT_OPTIONS.map((option) => (option.value === PLP_DEFAULT_SORT && defaultLabel ? { ...option, label: defaultLabel } : option));

// Desktop (lg+) sort: a native <select> styled as a brand field, sitting right-aligned above the grid.
// Native keeps keyboard/screen-reader behaviour for free and matches the filter checkboxes' approach.
export const PlpSortSelect = ({ value, defaultLabel }: PlpSortProps) => {
  const { setSort } = usePlpFilters();
  const options = optionsWithDefault(defaultLabel);
  return (
    <label className="hidden items-center gap-12 font-gt text-14 text-[#212121] lg:flex">
      <span className="font-medium">Sort by:</span>
      <span className="relative">
        <select
          value={value}
          onChange={(event) => setSort(event.target.value, PLP_DEFAULT_SORT)}
          className="h-44 cursor-pointer appearance-none border border-[#d7d7d7] bg-white bg-none pl-16 pr-44 text-14 font-medium outline-none transition-colors hover:bg-[#f0f0f0] focus-visible:border-[#1c1c1c]"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-16 top-1/2 size-16 -translate-y-1/2" />
      </span>
    </label>
  );
};

// Mobile (< lg) sort: a full-width button under the filter pills that opens a bottom sheet listing the
// options — the same sheet pattern the mobile filters use, so the two read as one control set.
export const PlpSortButton = ({ value, defaultLabel }: PlpSortProps) => {
  const [open, setOpen] = useState(false);
  const { setSort } = usePlpFilters();
  const options = optionsWithDefault(defaultLabel);
  const current = options.find((option) => option.value === value) ?? options[0];

  return (
    <div className="mt-12 font-gt text-[#212121] lg:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-44 w-full items-center justify-between border border-[#d7d7d7] px-16 text-14 font-medium transition-colors hover:bg-[#f0f0f0]"
      >
        <span>
          Sort: <span className="font-normal text-[#5e5e5e]">{current.label}</span>
        </span>
        <ChevronDownIcon className="size-16" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/30" onClick={() => setOpen(false)} aria-hidden />
          <div role="dialog" aria-label="Sort by" className="fixed inset-x-0 bottom-0 z-[70] flex max-h-[70vh] flex-col bg-white">
            <div className="flex items-center justify-between border-b border-[#d7d7d7] px-24 py-16">
              <span className="text-16 font-medium">Sort by</span>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="transition-opacity hover:opacity-70">
                <XMarkIcon className="size-24" />
              </button>
            </div>

            <ul className="flex flex-col gap-16 overflow-y-auto px-24 py-20">
              {options.map((option) => (
                <li key={option.value}>
                  <label className="flex min-h-44 cursor-pointer items-center gap-12 text-16">
                    <input
                      type="radio"
                      name="plp-sort"
                      value={option.value}
                      checked={option.value === current.value}
                      onChange={() => {
                        setSort(option.value, PLP_DEFAULT_SORT);
                        setOpen(false);
                      }}
                      className="size-16 shrink-0 cursor-pointer appearance-none rounded-full border border-[#9e9e9e] checked:border-[5px] checked:border-[#1c1c1c]"
                    />
                    {option.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
